import * as Sentry from '@sentry/react';

type SentryContext = {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  status?: number;
};

function isSuppressed(error: unknown): error is { suppressSentry: true } {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { suppressSentry?: boolean }).suppressSentry === true
  );
}

function parseRate(value?: string, fallback?: number): number | undefined {
  if (!value) {
    return fallback;
  }

  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    release: __APP_VERSION__.sha,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.httpClientIntegration(),
    ],
    tracesSampleRate: parseRate(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
      0,
    ),
    sampleRate: parseRate(import.meta.env.VITE_SENTRY_SAMPLE_RATE, 1.0),
    beforeSend(event) {
      return event;
    },
  });
}

export function setSentryUser(userId: string) {
  Sentry.setUser({ id: userId });
}

export function clearSentryUser() {
  Sentry.setUser(null);
}

export function captureError(error: unknown, context?: SentryContext) {
  if (error === null || error === undefined) {
    return;
  }

  // Expected product-signal responses (quota blocks from generation-gated
  // endpoints) carry `suppressSentry: true` — backend emits these as PostHog
  // `generation_blocked` events instead.
  if (isSuppressed(error)) {
    return;
  }

  const normalizedError =
    error instanceof Error ? error : new Error(String(error));

  Sentry.withScope((scope) => {
    if (context?.tags) {
      scope.setTags(context.tags);
    }
    if (context?.extra) {
      scope.setExtras(context.extra);
    }
    if (context?.status) {
      scope.setTag('http_status', String(context.status));
    }
    // Extract ApiError properties for searchability
    if (error && typeof error === 'object') {
      const apiErr = error as { status?: number; errorCode?: string; errorParams?: Record<string, unknown>; userMessage?: string };
      if (typeof apiErr.status === 'number') {
        scope.setTag('http_status', String(apiErr.status));
      }
      if (apiErr.errorCode) {
        scope.setTag('error_code', apiErr.errorCode);
      }
      if (apiErr.userMessage) {
        scope.setExtra('user_message', apiErr.userMessage);
      }
      if (apiErr.errorParams) {
        scope.setExtra('error_params', apiErr.errorParams);
      }
    }
    Sentry.captureException(normalizedError);
  });
}

export function addBreadcrumb(
  message: string,
  category?: string,
  data?: Record<string, unknown>,
) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
  });
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
